import { FC, ReactElement, useState } from 'react';

import { EvErrorCard, EvPopup } from '@evinced-private/ui-common';

import { useShowNewIssueDetailsModal } from 'src/common/hooks/useShowNewIssueDetailsModal';
import { DASHBOARD_ERROR_MSG } from 'src/common/pages/dashboards-container/errorMessages';

import { ShowDetailsCellFormatter } from './ShowDetailsCellFormatter';
import { ShowIssueDetailsCellFormatter } from './ShowIssueDetailsCellFormatter';

export interface IIssueDetailsBlockRenderProps {
	setError: (msg: string) => void;
	// Set only for the new modal: the block renders its own EvIssueDetailsNew dialog and closes it
	// through `onClose`. Otherwise the block returns bare content for the old DetailsPopup.
	isNewModal?: boolean;
	onClose?: () => void;
}

interface IIssueDetailsCellProps {
	infoTitle: string;
	issueType?: string;
	// Omit for products without a new-modal implementation (Mobile), so they stay on the old popup.
	supportsNewModal?: boolean;
	renderBlock: (props: IIssueDetailsBlockRenderProps) => ReactElement;
}

interface INewIssueDetailsPopupProps {
	renderBlock: (props: IIssueDetailsBlockRenderProps) => ReactElement;
	onClose: () => void;
}

// Mounted only while open, so the error state resets on every open.
const NewIssueDetailsPopup: FC<INewIssueDetailsPopupProps> = ({ renderBlock, onClose }) => {
	const [error, setError] = useState<string>(null);

	if (error) {
		return (
			<EvPopup isControlled isOpen onClose={onClose} title="Error">
				<EvErrorCard title="Error" contactUsMessage={DASHBOARD_ERROR_MSG} />
			</EvPopup>
		);
	}

	return renderBlock({ setError, isNewModal: true, onClose });
};

export const IssueDetailsCell: FC<IIssueDetailsCellProps> = ({
	infoTitle,
	issueType,
	supportsNewModal = false,
	renderBlock
}) => {
	const isNewModalEnabled = useShowNewIssueDetailsModal();
	const showNewIssueDetailsModal = supportsNewModal && isNewModalEnabled;

	if (!showNewIssueDetailsModal) {
		return (
			<ShowIssueDetailsCellFormatter
				infoTitle={infoTitle}
				issueType={issueType}
				renderDetailsBlock={(setError) => renderBlock({ setError })}
			/>
		);
	}

	return (
		<ShowDetailsCellFormatter
			infoTitle={infoTitle}
			ariaLabel="Show Issue Details"
			renderPopup={(closeModal) => (
				<NewIssueDetailsPopup renderBlock={renderBlock} onClose={closeModal} />
			)}
		/>
	);
};
